import { AcaModalService } from '@aca/ui';
import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { AuthService } from './auth.service';

/**
 * Native idle detection service that replaces @ng-idle/core for zoneless compatibility.
 *
 * Tracks user activity via document event listeners and uses setInterval for timeout
 * management. All state is signal-based so Angular's zoneless scheduler is notified
 * automatically on state changes.
 *
 * Thresholds:
 *   - 4 hours of inactivity → show session-timeout warning modal
 *   - 1 additional minute  → auto sign-out
 */
@Injectable({
  providedIn: 'root'
})
export class IdleService implements OnDestroy {
  private authService = inject(AuthService);
  private modalService = inject(AcaModalService);

  /** Idle threshold in milliseconds before the warning modal is shown (4 hours). */
  static readonly IDLE_WARN_MS = 60 * 60 * 4 * 1000;
  /** Grace period in milliseconds after the warning before auto sign-out (1 minute). */
  static readonly SIGN_OUT_AFTER_WARN_MS = 60 * 1000;

  /** DOM events that reset the idle timer and count as user activity. */
  private readonly ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
    'mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'wheel'
  ];

  private lastActivity = Date.now();
  private idleIntervalId: ReturnType<typeof setInterval> | null = null;
  private warnTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private signOutTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /** True while the service is actively watching for idle. */
  readonly watching = signal(false);

  /**
   * Bound activity handler — updates `lastActivity` and resets the sign-out
   * countdown if the user interacts during the warning period.
   */
  private readonly onActivity = (): void => {
    this.lastActivity = Date.now();

    // If the sign-out countdown is already running (warning modal is open),
    // cancel it, dismiss the modal, and restart the idle check from scratch.
    if (this.signOutTimeoutId !== null) {
      clearTimeout(this.signOutTimeoutId);
      this.signOutTimeoutId = null;
      this.modalService.closeLast();
      this.startIdleCheck();
    }
  };

  /**
   * Begin watching for user inactivity.
   * Safe to call multiple times — stops any previous watcher before starting a new one.
   */
  start(): void {
    this.stop();
    this.watching.set(true);
    this.lastActivity = Date.now();
    this.ACTIVITY_EVENTS.forEach(evt =>
      document.addEventListener(evt, this.onActivity, { passive: true })
    );
    this.startIdleCheck();
  }

  /**
   * Stop watching and cancel all pending timers.
   * Called on sign-out or manual teardown.
   */
  stop(): void {
    this.watching.set(false);
    this.clearTimers();
    this.ACTIVITY_EVENTS.forEach(evt =>
      document.removeEventListener(evt, this.onActivity)
    );
    this.modalService.closeLast();
  }

  /** Angular lifecycle hook — ensures all timers are cancelled when the service is destroyed. */
  ngOnDestroy(): void {
    this.stop();
  }

  // ─── private helpers ────────────────────────────────────────────────────────

  /**
   * Polls every 30 seconds to check whether the idle threshold has been exceeded.
   * Once the user goes idle, the interval is stopped and the warn/sign-out
   * sequence begins.
   */
  private startIdleCheck(): void {
    this.clearTimers();
    this.idleIntervalId = setInterval(() => {
      const idleMs = Date.now() - this.lastActivity;
      if (idleMs >= IdleService.IDLE_WARN_MS) {
        clearInterval(this.idleIntervalId!);
        this.idleIntervalId = null;
        this.showWarning();
      }
    }, 30_000);
  }

  /**
   * Display the session-timeout warning modal and start the auto sign-out countdown.
   * No-ops if the user is not currently authenticated.
   */
  private showWarning(): void {
    if (!this.authService.isAuthenticated()) return;

    this.modalService.alert('Session Timeout', 'Your session is about to expire.', [
      'Due to inactivity, you will be signed out of this application in approximately 1 minute.',
      'If you wish to continue, simply close this message.'
    ]);

    this.signOutTimeoutId = setTimeout(() => {
      this.stop();
      this.authService.signOut();
    }, IdleService.SIGN_OUT_AFTER_WARN_MS);
  }

  /** Cancel all active interval and timeout handles without touching event listeners. */
  private clearTimers(): void {
    if (this.idleIntervalId !== null) {
      clearInterval(this.idleIntervalId);
      this.idleIntervalId = null;
    }
    if (this.warnTimeoutId !== null) {
      clearTimeout(this.warnTimeoutId);
      this.warnTimeoutId = null;
    }
    if (this.signOutTimeoutId !== null) {
      clearTimeout(this.signOutTimeoutId);
      this.signOutTimeoutId = null;
    }
  }
}
